import React, { useState, useEffect } from "react";
import { gapi } from "gapi-script";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import GoogleIcon from "@mui/icons-material/Google";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addDays, addMonths, subMonths } from "date-fns";
import CalendarModal from "./CalendarModal";

// The scopes required to read and manage Google Calendar
const SCOPES = "https://www.googleapis.com/auth/calendar.events";
const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const DISCOVERY_DOCS = ["https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest"];

const CalendarWidget = () => {
  const [events, setEvents] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const initClient = async () => {
      try {
        let authInstance = gapi.auth2 && gapi.auth2.getAuthInstance();
        if (!authInstance) {
          await gapi.client.init({
            apiKey: API_KEY,
            clientId: CLIENT_ID,
            discoveryDocs: DISCOVERY_DOCS,
            scope: SCOPES,
          });
          authInstance = gapi.auth2.getAuthInstance();
        }
        authInstance.isSignedIn.listen(updateSigninStatus);
        updateSigninStatus(authInstance.isSignedIn.get());
      } catch (err) {
        console.error("Google API init error", err);
      }
    };
    
    gapi.load("client:auth2", initClient);
  }, []);

  const updateSigninStatus = (isSignedIn) => {
    const authInstance = gapi.auth2.getAuthInstance();
    const user = authInstance.currentUser.get();
    const hasCalendarScope = user.hasGrantedScopes(SCOPES);

    if (isSignedIn && hasCalendarScope) {
      setIsConnected(true);
      const profile = user.getBasicProfile();
      if (profile) {
        setUserProfile({
          imageUrl: profile.getImageUrl(),
          name: profile.getName(),
          email: profile.getEmail()
        });
      }
      listUpcomingEvents();
    } else {
      setIsConnected(false);
      setUserProfile(null);
      setEvents([]); // Local DB fallback later
    }
  };

  const handleAuthClick = async () => {
    try {
      const authInstance = gapi.auth2.getAuthInstance();
      const user = authInstance.currentUser.get();
      
      if (!user.hasGrantedScopes(SCOPES)) {
        await authInstance.signIn({ prompt: "consent", scope: SCOPES });
      } else if (!authInstance.isSignedIn.get()) {
        await authInstance.signIn({ prompt: "consent" });
      }
      updateSigninStatus(authInstance.isSignedIn.get());
    } catch (err) {
      console.error("Sign-in error:", err);
    }
  };

  const handleLogout = async () => {
    try {
      const authInstance = gapi.auth2.getAuthInstance();
      await authInstance.signOut();
      updateSigninStatus(false);
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  const listUpcomingEvents = async () => {
    setLoading(true);
    try {
      await gapi.client.load('calendar', 'v3');
      const response = await gapi.client.calendar.events.list({
        calendarId: 'primary',
        timeMin: (startOfMonth(new Date())).toISOString(),
        showDeleted: false,
        singleEvents: true,
        maxResults: 100,
        orderBy: 'startTime',
      });
      const googleEvents = response.result.items.map(event => ({
        id: event.id,
        title: event.summary,
        date: new Date(event.start.dateTime || event.start.date),
        isGoogle: true
      }));
      setEvents(googleEvents);
    } catch (err) {
      console.error("Error fetching Google Calendar events", err);
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const renderHeader = () => {
    return (
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <button onClick={prevMonth} className="text-gray-500 hover:bg-gray-100 rounded p-0.5"><ChevronLeftIcon fontSize="small" /></button>
          <span className="font-bold text-gray-800 text-sm">{format(currentDate, "MMMM yyyy")}</span>
          <button onClick={nextMonth} className="text-gray-500 hover:bg-gray-100 rounded p-0.5"><ChevronRightIcon fontSize="small" /></button>
        </div>
        <div className="flex items-center gap-1 text-gray-400">
          {!isConnected ? (
            <button 
              onClick={handleAuthClick} 
              className="flex items-center gap-1.5 text-[10px] font-bold px-2 py-1 bg-blue-50 text-blue-600 rounded-md border border-blue-100 hover:bg-blue-100 transition-colors mr-1"
              title="Connect Google Calendar"
            >
              <GoogleIcon style={{ fontSize: '12px' }} />
              Connect
            </button>
          ) : (
            userProfile && (
              <img 
                src={userProfile.imageUrl} 
                alt={userProfile.name} 
                title={`${userProfile.name} (${userProfile.email})`}
                className="w-6 h-6 rounded-full border border-gray-200 object-cover mr-1"
              />
            )
          )}
          <button
            onClick={() => setIsModalOpen(true)}
            style={{
              background: "transparent",
              border: "none",
              color: "#6C5DD3",
              fontWeight: 600,
              fontSize: "12px",
              cursor: "pointer",
              padding: "2px 0",
              letterSpacing: "-0.01em",
            }}
          >
            Open Calendar →
          </button>
        </div>
      </div>
    );
  };

  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(currentDate, { weekStartsOn: 1 });
    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="flex-1 text-center font-bold text-xs text-gray-400" key={i}>
          {format(addDays(startDate, i), "EE")}
        </div>
      );
    }
    return <div className="flex w-full mb-3">{days}</div>;
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const formattedDate = format(day, "d");
        const dayEvents = events.filter(e => isSameDay(e.date, cloneDay));
        const hasGoogle = dayEvents.some(e => e.isGoogle);

        days.push(
          <div
            className={`flex-1 flex flex-col items-center justify-center min-h-[48px] rounded-xl cursor-pointer relative mx-0.5 transition-colors
              ${!isSameMonth(day, monthStart) ? "text-transparent" : "text-gray-700"}
              ${isSameDay(day, selectedDate) ? "bg-[#4A3AFF] text-white font-bold shadow-md" : "hover:bg-gray-100"}
            `}
            key={day}
            onClick={() => {
              if (isSameMonth(cloneDay, monthStart)) {
                setSelectedDate(cloneDay);
              }
            }}
          >
            <span className="text-sm">{isSameMonth(day, monthStart) ? formattedDate : ""}</span>
            
            {/* Event Dots */}
            {isSameMonth(day, monthStart) && dayEvents.length > 0 && (
              <div className="absolute bottom-1.5 flex gap-1">
                 {hasGoogle && <div className={`w-1.5 h-1.5 rounded-full ${isSameDay(day, selectedDate) ? 'bg-white' : 'bg-blue-500'}`}></div>}
                 {dayEvents.length > (hasGoogle ? 1 : 0) && <div className={`w-1.5 h-1.5 rounded-full ${isSameDay(day, selectedDate) ? 'bg-indigo-200' : 'bg-purple-500'}`}></div>}
              </div>
            )}
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="flex w-full mb-1.5" key={day}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="flex flex-col">{rows}</div>;
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 w-full h-full flex flex-col justify-between">
        <div>
          {renderHeader()}
          {renderDays()}
          {renderCells()}
        </div>
      </div>

      <CalendarModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        events={events}
        refreshGoogleEvents={listUpcomingEvents}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        isConnected={isConnected}
        userProfile={userProfile}
        handleLogout={handleLogout}
      />
    </>
  );
};

export default CalendarWidget;
